/**
 * Offline POS Bridge — Marketing & Onboarding page.
 *
 * For users "stuck with their POS but no online activity."
 * Guides them through: register → download RetailsyncGo POS → or use Manual Sync.
 */

import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@retailsync/ui'
import { Button } from '@retailsync/ui'
import {
  ArrowUpTrayIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'

export function OfflinePosBridge() {
  return (
    <div className="space-y-8 p-6 max-w-4xl mx-auto">
      {/* Hero */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-4">
          <ExclamationTriangleIcon className="w-4 h-4" />
          Stuck with a POS that has no online features?
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Offline POS Bridge</h1>
        <p className="text-gray-500 mt-2 max-w-xl mx-auto">
          Your POS doesn't need an API or internet. Export your data, drop it here, and we handle the rest.
          We support <strong>every</strong> POS system — no exceptions.
        </p>
      </div>

      {/* Two paths */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Path 1: Manual Sync */}
        <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors">
          <CardHeader>
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
              <ArrowUpTrayIcon className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle>Manual File Import</CardTitle>
            <CardDescription>
              Export from your POS → Upload → We auto-map → Review → Sync
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Works with ANY POS export format (CSV, Excel)
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Smart auto-detection of 50+ column name variations
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Preview before sync — see exactly what changes
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Summary after sync — created, updated, skipped, errors
              </li>
            </ul>
            <Link to="/manual-sync">
              <Button className="w-full flex items-center justify-center gap-2">
                <ArrowUpTrayIcon className="w-4 h-4" />
                Start Manual Import
              </Button>
            </Link>
            <p className="text-xs text-gray-400 text-center">
              Perfect for: old POS terminals, Excel-based inventory, manual stock counts
            </p>
          </CardContent>
        </Card>

        {/* Path 2: Upgrade */}
        <Card className="border-2 border-green-200 bg-gradient-to-b from-green-50 to-white">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
                <CpuChipIcon className="w-6 h-6 text-white" />
              </div>
              <span className="px-2.5 py-0.5 rounded-full bg-green-200 text-green-800 text-xs font-bold">
                RECOMMENDED
              </span>
            </div>
            <CardTitle>Upgrade to RetailsyncGo POS</CardTitle>
            <CardDescription>
              Our Electron desktop app — free with your RetailsyncGo account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <strong>Auto-syncs at startup</strong> — no manual uploads ever
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <strong>Works offline</strong> — syncs when connection is restored
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <strong>Zero data loss</strong> — local SQLite DB + cloud backup
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <strong>Branch-aware</strong> — each terminal auto-links to its branch
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <strong>Free</strong> with your RetailsyncGo subscription
              </li>
            </ul>
            <a href="https://retailsync.africa" target="_blank" rel="noopener">
              <Button className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700">
                <CloudArrowUpIcon className="w-4 h-4" />
                Get RetailsyncGo POS
              </Button>
            </a>
            <p className="text-xs text-gray-400 text-center">
              Windows, macOS, Linux • Installs in under 2 minutes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle>How the Offline Bridge Works</CardTitle>
          <CardDescription>
            Three simple steps from any POS to your RetailsyncGo dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '1',
                icon: DocumentTextIcon,
                title: 'Export from Your POS',
                desc: 'Go to your POS and export products/inventory/customers as CSV or Excel. Save it anywhere on your computer. Any format works — we auto-detect everything.',
              },
              {
                step: '2',
                icon: ArrowUpTrayIcon,
                title: 'Upload to RetailsyncGo',
                desc: 'Drop your file into the Manual Sync page. Our smart mapper identifies your columns automatically. Review the preview, adjust if needed.',
              },
              {
                step: '3',
                icon: GlobeAltIcon,
                title: 'Live on Your Storefront',
                desc: 'After sync, your products are live on your online storefront. Inventory is tracked. Customers are in your ledger. Done.',
              },
            ].map(item => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-blue-600">{item.step}</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bottom CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-2">No POS left behind.</h2>
        <p className="text-blue-100 mb-4 max-w-lg mx-auto">
          If your POS can export a file, it works with RetailsyncGo. No API required. No internet required.
          That's our promise.
        </p>
        <div className="flex gap-3 justify-center">
          <Link to="/manual-sync">
            <Button className="bg-white text-blue-700 hover:bg-blue-50">
              Try Manual Sync Now →
            </Button>
          </Link>
          <Link to="/marketplace">
            <Button className="bg-white/10 text-white hover:bg-white/20 border border-white/20">
              Browse All Providers
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
